import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

type AuthMode = "login" | "signup";

export default function AdminLoginPage() {
  const [mode, setMode] = useState<AuthMode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        navigate("/admin/dashboard", { replace: true });
      } else {
        setChecking(false);
      }
    });
  }, [navigate]);

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setPasswordConfirm("");
    setError("");
    setMessage("");
  };

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
    resetForm();
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);

    const { error: loginError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (loginError) {
      setError(
        loginError.message === "Invalid login credentials"
          ? "이메일 또는 비밀번호가 올바르지 않습니다."
          : loginError.message
      );
      setLoading(false);
      return;
    }

    navigate("/admin/dashboard", { replace: true });
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (password !== passwordConfirm) {
      setError("비밀번호가 일치하지 않습니다.");
      return;
    }

    if (password.length < 6) {
      setError("비밀번호는 6자 이상이어야 합니다.");
      return;
    }

    setLoading(true);

    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
    });

    if (signUpError) {
      setError(
        signUpError.message === "User already registered"
          ? "이미 등록된 이메일입니다. 로그인해주세요."
          : signUpError.message
      );
      setLoading(false);
      return;
    }

    if (data.session) {
      navigate("/admin/dashboard", { replace: true });
      return;
    }

    setMessage("계정이 생성되었습니다! 로그인 화면으로 이동합니다.");
    setLoading(false);

    setTimeout(() => {
      switchMode("login");
    }, 1500);
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="flex items-center gap-3 text-foreground-500">
          <span className="w-5 h-5 flex items-center justify-center animate-spin">
            <i className="ri-loader-4-line" />
          </span>
          로딩 중...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-100 px-6">
      <div className="w-full max-w-[400px]">
        <div className="text-center mb-10">
          <span className="w-14 h-14 rounded-xl bg-foreground-950 flex items-center justify-center mx-auto">
            <i className="ri-shield-flash-line text-background-50 text-2xl" />
          </span>
          <h1 className="mt-5 text-2xl font-heading font-extrabold text-foreground-950">
            관리자 {mode === "login" ? "로그인" : "회원가입"}
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            한국고무공업 관리자 페이지
          </p>
        </div>

        <form
          onSubmit={mode === "login" ? handleLogin : handleSignUp}
          className="p-8 rounded-2xl bg-background-50 border border-background-200/70"
        >
          <div className="flex flex-col gap-5">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-2">
                이메일
              </label>
              <input
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="admin@example.com"
                className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-2">
                비밀번호
              </label>
              <input
                type="password"
                name="password"
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            {mode === "signup" && (
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-2">
                  비밀번호 확인
                </label>
                <input
                  type="password"
                  name="password_confirm"
                  autoComplete="new-password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
                />
              </div>
            )}

            {error && (
              <p className="text-xs text-red-600 bg-red-50 px-4 py-3 rounded-xl">
                {error}
              </p>
            )}

            {message && (
              <p className="text-xs text-emerald-600 bg-emerald-50 px-4 py-3 rounded-xl">
                {message}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3.5 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {loading && (
                <span className="w-4 h-4 flex items-center justify-center animate-spin">
                  <i className="ri-loader-4-line" />
                </span>
              )}
              {mode === "login" ? "로그인" : "회원가입"}
            </button>
          </div>
        </form>

        <p className="mt-6 text-center text-sm text-foreground-500">
          {mode === "login" ? (
            <>
              계정이 없으신가요?{" "}
              <button
                type="button"
                onClick={() => switchMode("signup")}
                className="text-primary-600 font-semibold hover:underline cursor-pointer"
              >
                회원가입
              </button>
            </>
          ) : (
            <>
              이미 계정이 있으신가요?{" "}
              <button
                type="button"
                onClick={() => switchMode("login")}
                className="text-primary-600 font-semibold hover:underline cursor-pointer"
              >
                로그인
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}